import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { X } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { useHotkey } from '../lib/hotkeys'
import { useIsPhone } from '../lib/useMediaQuery'
import { useSwipeDismiss } from '../lib/useSwipeDismiss'
import { Button } from './Button'
import { LayerProvider, useLayer, Z } from './context'
import { IconButton } from './IconButton'
import { backdropClass } from './Modal'

export interface DrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  /** 400 / 560 / 720 / 960 px */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** 'bottom' gilt unter 768 px automatisch (Sheet mit Griff) */
  side?: 'end' | 'bottom'
  /** Standard true; false = Seite bleibt bedienbar (Vorschau eines Datensatzes) */
  modal?: boolean
  /** feste Aktionsleiste: Abbrechen links, Hauptaktion rechts */
  footer?: ReactNode
  /** Schließen fragt nach, solange ungespeicherte Änderungen vorliegen */
  dirty?: boolean
  /** ⌘/Strg+Enter löst die Hauptaktion aus */
  onPrimaryAction?: () => void
  children: ReactNode
  'data-testid'?: string
}

const SIZE = { sm: 'md:w-100', md: 'md:w-140', lg: 'md:w-180', xl: 'md:w-240' } as const

/** Bearbeiten und Anlegen im Zusammenhang – der Kontext dahinter bleibt sichtbar. */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  side = 'end',
  modal = true,
  footer,
  dirty = false,
  onPrimaryAction,
  children,
  'data-testid': testId,
}: DrawerProps) {
  const zIndex = useLayer(Z.drawer)
  const [asking, setAsking] = useState(false)

  useEffect(() => {
    if (!open || !dirty) setAsking(false)
  }, [open, dirty])

  useHotkey('mod+enter', () => onPrimaryAction?.(), { enabled: open && Boolean(onPrimaryAction), allowInInputs: true })

  const requestClose = (): void => {
    if (dirty && !asking) setAsking(true)
    else onOpenChange(false)
  }

  const bottom = side === 'bottom'
  // Wischen schließt (Finger, Stift): das Sheet nach unten am Griff und Kopf, die Seitenleiste nach rechts
  const phone = useIsPhone()
  const sheet = bottom || phone
  const popup = useRef<HTMLDivElement | null>(null)
  const swipe = useSwipeDismiss(popup, sheet ? 'down' : 'right', requestClose, { enabled: modal })
  return (
    <BaseDialog.Root
      open={open}
      modal={modal ? true : false}
      disablePointerDismissal={!modal}
      onOpenChange={(next) => {
        if (next) onOpenChange(true)
        else requestClose()
      }}
    >
      <BaseDialog.Portal>
        {modal && <BaseDialog.Backdrop className={backdropClass} style={{ zIndex }} />}
        <BaseDialog.Viewport
          data-ui="dialog-frame"
          className={cn(
            // Rahmen = sichtbarer Bereich: bei offener Tastatur Kopf unter der Statusleiste, Fußleiste über der Tastatur
            'visible-frame pointer-events-none flex items-end justify-center pt-[max(3rem,calc(env(safe-area-inset-top)+0.5rem))]',
            !bottom && 'md:items-stretch md:justify-end md:pt-0',
          )}
          style={{ zIndex }}
        >
          <BaseDialog.Popup
            ref={popup}
            {...(sheet ? {} : swipe)}
            data-testid={testId}
            data-ui="drawer"
            className={cn(
              'pointer-events-auto flex max-h-full w-full flex-col rounded-t-lg border border-line bg-overlay text-fg shadow-lg outline-none',
              'transition-[opacity,translate] duration-(--duration-sheet) ease-out data-ending-style:duration-(--duration-base) data-ending-style:ease-in',
              'data-ending-style:translate-y-full data-starting-style:translate-y-full',
              !bottom &&
                cn(
                  SIZE[size],
                  // höchstens 90 % der Breite (Tablet hoch); in der App frei von Statusleiste und Kamera-Aussparung
                  'md:h-full md:max-w-[90vw] md:touch-pan-y md:rounded-none md:border-y-0 md:border-r-0 md:pt-[env(safe-area-inset-top)] md:pr-[env(safe-area-inset-right)] md:duration-(--duration-slow)',
                  'md:data-ending-style:translate-x-4 md:data-ending-style:translate-y-0 md:data-ending-style:opacity-0 md:data-starting-style:translate-x-4 md:data-starting-style:translate-y-0 md:data-starting-style:opacity-0',
                ),
            )}
          >
            <LayerProvider z={zIndex}>
              <div className={cn('flex shrink-0 flex-col', sheet && 'touch-none')} {...(sheet ? swipe : {})}>
              <span
                className={cn('mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-line-strong', !bottom && 'md:hidden')}
                aria-hidden
              />
              <header className="flex shrink-0 items-start gap-3 border-b border-line px-5 pt-3 pb-3 md:pt-4">
                <div className="min-w-0 flex-1">
                  <BaseDialog.Title className="text-title text-fg">{title}</BaseDialog.Title>
                  {description && (
                    <BaseDialog.Description className="mt-0.5 text-dense text-fg-muted">
                      {description}
                    </BaseDialog.Description>
                  )}
                </div>
                <IconButton
                  icon={X}
                  label="Schließen"
                  size="sm"
                  tooltip={false}
                  // Touch: 44 × 44 px (das Blatt am Telefon), sonst die kleine Kontrollhöhe
                  className="-mr-1.5 touch:size-11"
                  onClick={requestClose}
                />
              </header>
              </div>
              <div className={cn('min-h-0 flex-1 overflow-y-auto px-5 pt-5', footer || asking ? 'pb-5' : 'pb-[max(1.25rem,env(safe-area-inset-bottom))]')}>
                {children}
              </div>
              {asking ? (
                <footer
                  role="alertdialog"
                  aria-label="Ungespeicherte Änderungen"
                  className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-warning-line bg-warning-subtle px-5 pt-3 pb-safe-bar"
                >
                  <p className="text-dense text-fg">Es gibt ungespeicherte Änderungen. Verwerfen?</p>
                  <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => setAsking(false)} autoFocus>
                      Weiter bearbeiten
                    </Button>
                    <Button variant="danger" onClick={() => onOpenChange(false)}>
                      Änderungen verwerfen
                    </Button>
                  </div>
                </footer>
              ) : (
                footer && (
                  <footer className="flex shrink-0 items-center justify-between gap-2 border-t border-line bg-overlay px-5 pt-3 pb-safe-bar">
                    {footer}
                  </footer>
                )
              )}
            </LayerProvider>
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  )
}
